import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Plus, X, ChevronRight, ChevronLeft, Search, Check, CheckCircle2, Layers, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import type { UpdateRequest, Agency, Contact } from '../types';

type Step = 1 | 2 | 3 | 4;

interface BulkRecipient {
  agency: Agency;
  recipient_email: string;
  recipient_name: string;
}

interface CreateForm {
  agency: Agency | null;
  recipient_email: string;
  recipient_name: string;
  recipient_use_other: boolean;
  selected_contact_ids: Set<string>;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  Pending:   { bg: 'var(--sky-50)', color: 'var(--electric)' },
  Submitted: { bg: 'var(--amber-50)', color: 'var(--amber-text)' },
  Approved:  { bg: 'var(--green-50)', color: 'var(--green-700)' },
  Rejected:  { bg: 'var(--red-50)', color: 'var(--red-700)' },
  Expired:   { bg: 'var(--gray-100)', color: 'var(--gray-500)' },
};

const STEP_LABELS = ['Agency', 'Contacts', 'Review'] as const;

function StepIndicator({ step }: { step: Step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
      {STEP_LABELS.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                background: done ? 'var(--green)' : active ? 'var(--electric)' : 'transparent',
                color: done || active ? 'white' : 'var(--gray-400)',
                border: done || active ? 'none' : '2px solid var(--gray-300)',
                flexShrink: 0,
              }}>
                {done ? <Check size={12} strokeWidth={3} /> : idx}
              </div>
              <span style={{
                fontSize: 12, fontWeight: active ? 600 : 400,
                color: done ? 'var(--green)' : active ? 'var(--electric)' : 'var(--gray-500)',
              }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: done ? 'var(--green)' : 'var(--gray-200)', margin: '0 4px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const EMPTY_FORM: CreateForm = {
  agency: null,
  recipient_email: '',
  recipient_name: '',
  recipient_use_other: false,
  selected_contact_ids: new Set(),
};

const UpdateRequestsPage: React.FC = () => {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [agencySearch, setAgencySearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [sentTo, setSentTo] = useState<{ name: string; count: number; pin?: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'all'>('active');
  const [preselectAgencyId, setPreselectAgencyId] = useState<string | null>(null);
  const [preselectContactId, setPreselectContactId] = useState<string | null>(null);
  // Optional: missing role(s) carried in from the KPI "Fix gaps" button (B28).
  // Purely informational guidance for the composer — not persisted on the request.
  const [requestedRoles, setRequestedRoles] = useState<string[]>([]);

  // Bulk send state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState<1 | 2 | 3>(1);
  const [bulkAgencySearch, setBulkAgencySearch] = useState('');
  const [bulkSelectedAgencies, setBulkSelectedAgencies] = useState<Set<string>>(new Set());
  const [bulkRecipients, setBulkRecipients] = useState<BulkRecipient[]>([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResults, setBulkResults] = useState<{ agency: string; ok: boolean; err?: string }[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['update-requests', activeTab],
    queryFn: () =>
      activeTab === 'active'
        ? api.getUpdateRequests({ status_id: 1, active_only: true })
        : api.getUpdateRequests(),
  });

  const requests: UpdateRequest[] = data?.data ?? [];
  const total: number = data?.meta?.total ?? requests.length;

  const { data: agenciesData } = useQuery({
    queryKey: ['agencies-modal', showCreate, showBulkModal],
    queryFn: () => api.getAgencies({ limit: 200 }),
    enabled: showCreate || showBulkModal,
    staleTime: 0,
  });
  const allAgencies: Agency[] = agenciesData?.data ?? [];
  const filteredAgencies = agencySearch.trim()
    ? allAgencies.filter((a) =>
        a.name.toLowerCase().includes(agencySearch.toLowerCase()) ||
        (a.acronym ?? '').toLowerCase().includes(agencySearch.toLowerCase())
      )
    : allAgencies;

  const { data: agencyContactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts-for-agency', form.agency?.id],
    queryFn: () => api.searchContacts({ agency_id: form.agency!.id, limit: 200 }),
    enabled: !!form.agency?.id && (step === 2 || step === 3),
  });
  const agencyContacts: Contact[] = agencyContactsData?.data ?? [];

  const filteredContacts = contactSearch.trim()
    ? agencyContacts.filter((c) => {
        const name = (c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`).toLowerCase();
        return name.includes(contactSearch.toLowerCase()) ||
               (c.title ?? '').toLowerCase().includes(contactSearch.toLowerCase());
      })
    : agencyContacts;

  // Liaisons and Alternates for recipient dropdown
  const liaisons = agencyContacts.filter((c) => {
    const roles = (c as any).roles as string[] | undefined;
    const roleNames = (c as any).role_names as string[] | undefined;
    const allRoles = [...(roles ?? []), ...(roleNames ?? [])];
    if (allRoles.length) {
      return allRoles.some((r) =>
        r.toLowerCase().includes('liaison') ||
        r.toLowerCase().includes('alternate')
      );
    }
    return (c.title ?? '').toLowerCase().includes('liaison') ||
           (c.title ?? '').toLowerCase().includes('alternate');
  });

  React.useEffect(() => {
    if (step === 2 && agencyContacts.length > 0 && form.selected_contact_ids.size === 0) {
      if (preselectContactId) {
        setForm((f) => ({ ...f, selected_contact_ids: new Set([preselectContactId]) }));
      } else {
        setForm((f) => ({ ...f, selected_contact_ids: new Set(agencyContacts.map((c) => c.id)) }));
      }
    }
  }, [agencyContacts, step]); // eslint-disable-line

  // Pre-fill recipient from first liaison when contacts load
  React.useEffect(() => {
    if (step === 3 && liaisons.length > 0 && !form.recipient_email && !form.recipient_use_other) {
      const first = liaisons[0];
      setForm((f) => ({
        ...f,
        recipient_email: first.email_primary ?? '',
        recipient_name: first.full_name || `${first.first_name ?? ''} ${first.last_name ?? ''}`.trim(),
      }));
    }
  }, [step, liaisons.length]); // eslint-disable-line

  // Auto-open create modal when navigated here with ?create=1
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      const agencyId = searchParams.get('agency_id');
      const contactId = searchParams.get('contact_id');
      const roles = searchParams.get('roles');
      resetForm();
      setShowCreate(true);
      setSearchParams({}, { replace: true });
      if (agencyId) setPreselectAgencyId(agencyId);
      if (contactId) setPreselectContactId(contactId);
      setRequestedRoles(
        roles ? roles.split(',').map((r) => r.trim()).filter(Boolean) : []
      );
    }
  }, []); // eslint-disable-line

  // Pre-select agency (and optionally contact) once agencies load
  useEffect(() => {
    if (!preselectAgencyId || allAgencies.length === 0 || !showCreate) return;
    const agency = allAgencies.find((a) => a.id === preselectAgencyId);
    if (!agency) return;

    if (preselectContactId) {
      // "This contact only" — jump directly to Step 3 with agency + contact pre-populated
      setForm((f) => ({
        ...f,
        agency,
        selected_contact_ids: new Set([preselectContactId]),
        recipient_email: '',
        recipient_name: '',
        recipient_use_other: false,
      }));
      setStep(3);
      setPreselectAgencyId(null);
      setPreselectContactId(null);
    } else {
      // Agency-only preselect — go to Step 2 as before
      handleSelectAgency(agency);
      setPreselectAgencyId(null);
    }
  }, [allAgencies, preselectAgencyId, preselectContactId, showCreate]); // eslint-disable-line

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelUpdateRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['update-requests'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createUpdateRequest({
        agency_id:       form.agency!.id,
        recipient_email: form.recipient_email,
        recipient_name:  form.recipient_name,
        contact_ids:     Array.from(form.selected_contact_ids),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['update-requests'] });
      window.dispatchEvent(new Event('iad2:counts-stale'));
      setSentTo({ name: form.recipient_name, count: form.selected_contact_ids.size, pin: (res?.data as any)?.pin });
      setStep(4);
    },
  });

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setStep(1);
    setAgencySearch('');
    setContactSearch('');
    setSentTo(null);
    setPreselectAgencyId(null);
    setPreselectContactId(null);
    setRequestedRoles([]);
  };

  const openCreate = () => { resetForm(); setShowCreate(true); };
  const closeModal = () => { setShowCreate(false); resetForm(); };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkStep(1);
    setBulkAgencySearch('');
    setBulkSelectedAgencies(new Set());
    setBulkRecipients([]);
    setBulkResults([]);
    setBulkSending(false);
  };

  const bulkFilteredAgencies = bulkAgencySearch.trim()
    ? allAgencies.filter((a) =>
        a.name.toLowerCase().includes(bulkAgencySearch.toLowerCase()) ||
        (a.acronym ?? '').toLowerCase().includes(bulkAgencySearch.toLowerCase())
      )
    : allAgencies;

  const toggleBulkAgency = (id: string) => {
    setBulkSelectedAgencies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const buildBulkRecipients = async () => {
    const selected = allAgencies.filter((a) => bulkSelectedAgencies.has(a.id));
    const recipients: BulkRecipient[] = [];
    for (const agency of selected) {
      let recipientEmail = '';
      let recipientName = '';
      try {
        const contacts = await api.searchContacts({ agency_id: agency.id, limit: 50 });
        const all: Contact[] = contacts?.data ?? [];
        const liaison = all.find((c) => {
          const roles = (c as any).roles as string[] | undefined;
          const roleNames = (c as any).role_names as string[] | undefined;
          const combined = [...(roles ?? []), ...(roleNames ?? [])];
          if (combined.length) {
            return combined.some((r) =>
              r.toLowerCase().includes('liaison') || r.toLowerCase().includes('alternate')
            );
          }
          return (c.title ?? '').toLowerCase().includes('liaison') ||
                 (c.title ?? '').toLowerCase().includes('alternate');
        }) ?? all[0];
        if (liaison) {
          recipientEmail = liaison.email_primary ?? '';
          recipientName = liaison.full_name || `${liaison.first_name ?? ''} ${liaison.last_name ?? ''}`.trim();
        }
      } catch {}
      recipients.push({ agency, recipient_email: recipientEmail, recipient_name: recipientName });
    }
    setBulkRecipients(recipients);
    setBulkStep(2);
  };

  const sendBulkRequests = async () => {
    setBulkSending(true);
    const results: { agency: string; ok: boolean; err?: string }[] = [];
    for (const r of bulkRecipients) {
      if (!r.recipient_email.trim()) {
        results.push({ agency: r.agency.name, ok: false, err: 'No recipient email' });
        continue;
      }
      try {
        await api.createUpdateRequest({
          agency_id:       r.agency.id,
          recipient_email: r.recipient_email,
          recipient_name:  r.recipient_name,
        });
        results.push({ agency: r.agency.name, ok: true });
      } catch (err: any) {
        results.push({ agency: r.agency.name, ok: false, err: err?.response?.data?.error ?? 'Failed' });
      }
    }
    setBulkResults(results);
    setBulkSending(false);
    setBulkStep(3);
    qc.invalidateQueries({ queryKey: ['update-requests'] });
    window.dispatchEvent(new Event('iad2:counts-stale'));
  };

  const handleSelectAgency = (agency: Agency) => {
    setForm((f) => ({ ...f, agency, selected_contact_ids: new Set(), recipient_email: '', recipient_name: '', recipient_use_other: false }));
    setStep(2);
  };

  const toggleContact = (id: string) => {
    setForm((f) => {
      const next = new Set(f.selected_contact_ids);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...f, selected_contact_ids: next };
    });
  };

  const handleRecipientSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === '__other__') {
      setForm((f) => ({ ...f, recipient_use_other: true, recipient_email: '', recipient_name: '' }));
    } else {
      const contact = liaisons.find((c) => c.email_primary === val);
      setForm((f) => ({
        ...f,
        recipient_use_other: false,
        recipient_email: val,
        recipient_name: contact
          ? (contact.full_name || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim())
          : f.recipient_name,
      }));
    }
  };

  const isExpired = (req: UpdateRequest) => {
    const statusName = (req as any).status_name ?? req.status;
    return new Date(req.expires_at) < new Date() && statusName === 'Pending';
  };

  const getCompletedDate = (req: UpdateRequest): string | null => {
    const s = ((req as any).status_name ?? req.status)?.toLowerCase();
    if (isExpired(req) || s === 'expired') return req.expires_at;
    if (s === 'submitted' || s === 'approved') return (req as any).submitted_at ?? null;
    if (s === 'cancelled') return (req as any).reviewed_at ?? null;
    return null;
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          Update Requests
          {total > 0 && (
            <span style={{ fontSize: 14, color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>({total})</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => { closeBulkModal(); setShowBulkModal(true); }}>
            <Layers size={14} />
            Bulk Send
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={14} />
            New Request
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['active', 'all'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'btn btn-primary' : 'btn btn-secondary'}
            style={{ fontSize: 14, padding: '5px 14px' }}
          >
            {tab === 'active' ? 'Active' : 'All'}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Agency</th>
              <th>Recipient</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Created</th>
              {activeTab === 'all' && <th>Completed</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={activeTab === 'all' ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>Loading…</td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={activeTab === 'all' ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>No update requests</td>
              </tr>
            ) : (
              requests.map((r) => {
                const statusName = (r as any).status_name ?? r.status;
                const colors = STATUS_COLORS[statusName] ?? { bg: 'var(--gray-100)', color: 'var(--gray-500)' };
                const expired = isExpired(r);
                return (
                  <tr key={r.id}>
                    <td>{r.agency_name ? <span className="chip">{r.agency_name}</span> : '—'}</td>
                    <td style={{ fontSize: 14 }}>{r.recipient_email}</td>
                    <td>
                      <span className="pill" style={{ background: expired ? 'var(--gray-100)' : colors.bg, color: expired ? 'var(--gray-500)' : colors.color }}>
                        {expired ? 'Expired' : statusName}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      <div style={{ color: expired ? 'var(--red)' : 'var(--gray-500)' }}>
                        {new Date(r.expires_at).toLocaleDateString()}
                      </div>
                      {(() => {
                        if (!r.expires_at || statusName !== 'Pending') return null;
                        const now = new Date(); now.setHours(0,0,0,0);
                        const exp = new Date(r.expires_at); exp.setHours(0,0,0,0);
                        const days = Math.ceil((exp.getTime() - now.getTime()) / 86400000);
                        if (days <= 0) return <div style={{ color: 'var(--red)', fontWeight: 600 }}>Expired</div>;
                        if (days === 1) return <div style={{ color: 'var(--amber-text)', fontWeight: 600 }}>Expires tomorrow</div>;
                        if (days <= 3) return <div style={{ color: 'var(--amber-text)' }}>Expires in {days} days</div>;
                        return <div style={{ color: 'var(--gray-500)' }}>Expires in {days} days</div>;
                      })()}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    {activeTab === 'all' && (
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {(() => { const d = getCompletedDate(r); return d ? new Date(d).toLocaleDateString() : '—'; })()}
                      </td>
                    )}
                    <td>
                      {statusName === 'Pending' && !expired && (
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: 12, padding: '4px 10px', color: 'var(--red)', border: '1px solid var(--red)', background: 'transparent' }}
                          disabled={cancelMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Cancel this update request to ${r.recipient_email}? The link and PIN will stop working.`)) {
                              cancelMutation.mutate(r.id);
                            }
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk Send Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={closeBulkModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Bulk Send Update Requests</h3>
              <button className="btn btn-ghost" onClick={closeBulkModal}><X size={16} /></button>
            </div>

            {/* Step 1: Select agencies */}
            {bulkStep === 1 && (
              <>
                <p style={{ fontSize: 14, color: 'var(--gray-600)', margin: '0 0 10px' }}>
                  Select agencies to send update requests to.
                </p>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                  <input
                    className="field-input"
                    style={{ paddingLeft: 32 }}
                    autoFocus
                    placeholder="Search agencies…"
                    value={bulkAgencySearch}
                    onChange={(e) => setBulkAgencySearch(e.target.value)}
                  />
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, marginBottom: 12 }}>
                  {bulkFilteredAgencies.map((a) => (
                    <label
                      key={a.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={bulkSelectedAgencies.has(a.id)}
                        onChange={() => toggleBulkAgency(a.id)}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{a.name}</div>
                        {a.acronym && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.acronym}</div>}
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{
                  background: 'var(--sky-50)',
                  border: '1px solid var(--navy)',
                  borderRadius: 6,
                  padding: '10px 14px',
                  fontSize: 12,
                  color: 'var(--electric)',
                  marginBottom: 12,
                }}>
                  ℹ All contacts at each selected agency will be included for verification.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {bulkSelectedAgencies.size} selected
                  </span>
                  <button
                    className="btn btn-primary"
                    disabled={bulkSelectedAgencies.size === 0}
                    onClick={buildBulkRecipients}
                  >
                    Configure Recipients <ChevronRight size={14} />
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Per-agency recipient config */}
            {bulkStep === 2 && (
              <>
                <p style={{ fontSize: 14, color: 'var(--gray-600)', margin: '0 0 10px' }}>
                  Review recipients for each agency. Pre-filled from liaisons where available.
                </p>
                <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 16 }}>
                  {bulkRecipients.map((r, i) => (
                    <div key={r.agency.id} style={{ marginBottom: 12, padding: '10px 14px', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>{r.agency.name}</div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <input
                          className="field-input"
                          placeholder="Recipient name"
                          value={r.recipient_name}
                          style={{ fontSize: 14 }}
                          onChange={(e) => {
                            const next = [...bulkRecipients];
                            next[i] = { ...next[i], recipient_name: e.target.value };
                            setBulkRecipients(next);
                          }}
                        />
                        <input
                          className="field-input"
                          type="email"
                          placeholder="recipient@agency.gov"
                          value={r.recipient_email}
                          style={{ fontSize: 14 }}
                          onChange={(e) => {
                            const next = [...bulkRecipients];
                            next[i] = { ...next[i], recipient_email: e.target.value };
                            setBulkRecipients(next);
                          }}
                        />
                      </div>
                      {!r.recipient_email && (
                        <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>No email — request will be skipped</div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button className="btn btn-ghost" onClick={() => setBulkStep(1)}>
                    <ChevronLeft size={14} /> Back
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={bulkSending || bulkRecipients.every((r) => !r.recipient_email.trim())}
                    onClick={sendBulkRequests}
                  >
                    {bulkSending
                      ? `Sending ${bulkResults.length + 1} of ${bulkRecipients.length}…`
                      : `Send ${bulkRecipients.filter((r) => r.recipient_email.trim()).length} Requests`}
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Results */}
            {bulkStep === 3 && (
              <>
                <div style={{ marginBottom: 16 }}>
                  {bulkResults.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                        borderBottom: '1px solid var(--gray-100)', fontSize: 14,
                      }}
                    >
                      <span style={{ color: r.ok ? 'var(--green)' : 'var(--red)' }}>
                        {r.ok ? '✓' : '✗'}
                      </span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{r.agency}</span>
                      {!r.ok && <span style={{ color: 'var(--red)', fontSize: 12 }}>{r.err}</span>}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 16 }}>
                  {bulkResults.filter((r) => r.ok).length} sent · {bulkResults.filter((r) => !r.ok).length} skipped or failed
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" onClick={closeBulkModal}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 540, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>New Update Request</h3>
              <button className="btn btn-ghost" onClick={closeModal}><X size={16} /></button>
            </div>

            {/* Step 4: Success confirmation */}
            {step === 4 && sentTo && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <CheckCircle2 size={48} color="var(--green)" style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Update request sent
                </div>
                <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 4 }}>
                  Email sent to <strong>{sentTo.name}</strong>
                </div>
                <div style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: sentTo.pin ? 16 : 24 }}>
                  {sentTo.count} contact{sentTo.count !== 1 ? 's' : ''} included · Expires in 14 days
                </div>
                {sentTo.pin && (
                  <div style={{
                    background: 'var(--sky-50)', border: '2px solid var(--electric)', borderRadius: 8,
                    padding: '16px 24px', marginBottom: 24, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--electric)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                      Access PIN (included in email)
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: '.3em', color: 'var(--electric)', fontFamily: 'monospace' }}>
                      {sentTo.pin}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 8 }}>
                      The recipient will need this PIN to access the portal.
                    </div>
                  </div>
                )}
                <button className="btn btn-secondary" onClick={closeModal}>Close</button>
              </div>
            )}

            {step < 4 && <StepIndicator step={step} />}

            {/* B28: missing-role guidance carried in from the KPI "Fix gaps" button */}
            {step < 4 && requestedRoles.length > 0 && (
              <div style={{
                background: 'var(--amber-50)',
                border: '1px solid var(--amber)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 12.5,
                color: 'var(--amber-text)',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  This agency is missing{' '}
                  <strong>{requestedRoles.join(', ')}</strong>.
                  Include {requestedRoles.length > 1 ? 'contacts for these roles' : 'a contact for this role'} so the
                  agency knows what to fill.
                </span>
              </div>
            )}

            {/* Step 1: Agency */}
            {step === 1 && (
              <>
                <p style={{ fontSize: 14, color: 'var(--gray-600)', margin: '0 0 12px' }}>
                  Select the agency to send a contact update request to.
                </p>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                  <input
                    className="field-input"
                    style={{ paddingLeft: 32 }}
                    autoFocus
                    placeholder="Search agencies…"
                    value={agencySearch}
                    onChange={(e) => setAgencySearch(e.target.value)}
                  />
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                  {filteredAgencies.map((a) => (
                    <div
                      key={a.id}
                      style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onClick={() => handleSelectAgency(a)}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{a.name}</div>
                        {a.acronym && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.acronym}</div>}
                      </div>
                      <ChevronRight size={14} color="var(--gray-400)" />
                    </div>
                  ))}
                  {filteredAgencies.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', fontSize: 14, color: 'var(--gray-500)' }}>No agencies found</div>
                  )}
                </div>
                <div style={{
                  background: 'var(--sky-50)',
                  border: '1px solid var(--navy)',
                  borderRadius: 6,
                  padding: '10px 14px',
                  fontSize: 12,
                  color: 'var(--electric)',
                  marginTop: 12,
                }}>
                  ℹ All contacts at each selected agency will be included for verification. You can select specific contacts after choosing an agency.
                </div>
              </>
            )}

            {/* Step 2: Select contacts */}
            {step === 2 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setStep(1)}>
                    <ChevronLeft size={14} /> Back
                  </button>
                  <span style={{ fontSize: 14 }}>
                    <strong>{form.agency?.name}</strong> — select contacts to include
                  </span>
                </div>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                  <input
                    className="field-input"
                    style={{ paddingLeft: 32 }}
                    placeholder="Filter contacts…"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                  />
                </div>
                {preselectContactId && (
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8, fontStyle: 'italic' }}>
                    Showing 1 contact — requested for this contact only
                  </div>
                )}
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, marginBottom: 12 }}>
                  {contactsLoading ? (
                    <div style={{ padding: 20, textAlign: 'center', fontSize: 14, color: 'var(--gray-500)' }}>Loading contacts…</div>
                  ) : filteredContacts.filter((c) => !preselectContactId || c.id === preselectContactId).length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', fontSize: 14, color: 'var(--gray-500)' }}>No contacts found</div>
                  ) : filteredContacts.filter((c) => !preselectContactId || c.id === preselectContactId).map((c) => {
                    const roles = (c as any).roles as string[] | undefined;
                    const roleLabel = roles?.[0] ?? c.title ?? '';
                    const fullName = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
                    return (
                      <label
                        key={c.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={form.selected_contact_ids.has(c.id)}
                          onChange={() => toggleContact(c.id)}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{fullName}</div>
                          {roleLabel && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{roleLabel}</div>}
                        </div>
                        {(c as any).classification_id === 2 && (
                          <span className="pill pill-restricted" style={{ fontSize: 12 }}>Restricted</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {form.selected_contact_ids.size} of {agencyContacts.length} selected
                  </span>
                  <button className="btn btn-primary" onClick={() => setStep(3)}>
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Recipient & Send */}
            {step === 3 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setStep(2)}>
                    <ChevronLeft size={14} /> Back
                  </button>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>Review and send</span>
                </div>

                <div style={{ background: 'var(--gray-50)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
                  <span style={{ color: 'var(--gray-500)' }}>Agency: </span>
                  <strong>{form.agency?.name}</strong>
                  <span style={{ color: 'var(--gray-500)', marginLeft: 12 }}>
                    {form.selected_contact_ids.size} contact{form.selected_contact_ids.size !== 1 ? 's' : ''}
                  </span>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label className="field-label">Send to</label>
                  {liaisons.length > 0 ? (
                    <>
                      <select
                        className="field-input"
                        value={form.recipient_use_other ? '__other__' : form.recipient_email}
                        onChange={handleRecipientSelect}
                      >
                        {liaisons.map((c) => (
                          <option key={c.id} value={c.email_primary ?? ''}>
                            {c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()} — {c.email_primary ?? '(no email)'}
                            {c.title ? ` · ${c.title}` : ''}
                          </option>
                        ))}
                        <option value="__other__">Other (enter manually)</option>
                      </select>
                    </>
                  ) : null}
                  {liaisons.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                      No liaisons found for this agency. Enter recipient email manually.
                    </div>
                  )}
                  {(form.recipient_use_other || liaisons.length === 0) && (
                    <div style={{ marginTop: liaisons.length > 0 ? 8 : 0 }}>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="Recipient full name"
                        value={form.recipient_name}
                        onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                        style={{ marginBottom: 8 }}
                        autoFocus
                      />
                      <input
                        className="field-input"
                        type="email"
                        placeholder="email@agency.gov"
                        value={form.recipient_email}
                        onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                      />
                    </div>
                  )}
                </div>

                {createMutation.isError && (
                  <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 12px' }}>
                    {(createMutation.error as any)?.response?.data?.error ?? 'Failed to send request.'}
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    disabled={!form.recipient_email.trim() || !form.recipient_name.trim() || createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                  >
                    {createMutation.isPending ? 'Sending…' : 'Send Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateRequestsPage;
